import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Hand } from '../components/Hand';
import { Table } from '../components/Table';
import { Seat } from '../components/Seat';
import { PlayingCard } from '../components/PlayingCard';
import { Avatar } from '../components/Avatar';
import { Confetti } from '../components/Confetti';
import { GameOptionsPanel } from '../components/GameOptionsPanel';
import { useGame } from '../hooks/useGame';
import { useSettings } from '../hooks/useSettings';
import { useSession } from '../hooks/useSession';
import { useActiveGame } from '../hooks/useActiveGame';
import { useReactions } from '../hooks/useReactions';
import { useFriends } from '../hooks/useFriends';
import { ReactionPicker } from '../components/Reactions';
import { ReactionArt } from '../components/ReactionArt';
import { TurnCountdownRing } from '../components/TurnCountdownRing';
import type { ReactionKind } from '@durak/shared';
import { getSocket } from '../lib/socket';
import { api } from '../lib/api';
import type { Card, Move, PlayerView } from '@durak/shared';
import { beats } from '@durak/shared';

/** Mirrors the engine's doTransfer preconditions so the client can pick
 *  transfer automatically when defend isn't possible with the swiped card. */
function canTransferWith(view: PlayerView, card: Card): boolean {
  if (!view.options.transferable) return false;
  if (view.table.length === 0) return false;
  if (view.table.some(s => s.defense)) return false;
  if (card.rank !== view.table[0].attack.rank) return false;
  const n = view.players.length;
  for (let i = 1; i <= n; i++) {
    const seat = (view.yourSeat + i) % n;
    const p = view.players[seat];
    if (p.finished) continue;
    return p.handCount >= view.table.length + 1;
  }
  return false;
}
import { motion, AnimatePresence } from 'framer-motion';
import { haptic } from '../lib/haptic';
import { playSfx } from '../lib/sound';
import clsx from 'clsx';

export default function GamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const nav = useNavigate();
  const { view: storeView, attach, chat, error, setError, setView, clearChat } = useGame();
  // The store may still hold the previous game's view (or a finished game we
  // already left) — never render or act on a board other than the route's.
  const view = storeView && storeView.gameId === gameId ? storeView : null;
  const settings = useSettings();
  const me = useSession(s => s.user);
  const refreshActive = useActiveGame(s => s.refresh);
  const clearActive = useActiveGame(s => s.clear);
  const attachReactions = useReactions(s => s.attach);
  const reactions = useReactions(s => s.reactions);

  const [selected, setSelected] = useState<Card | null>(null);
  const [draggingCard, setDraggingCard] = useState<Card | null>(null);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const slotRectsRef = useRef<Map<number, HTMLElement>>(new Map());
  const tableRectRef = useRef<HTMLElement | null>(null);

  useEffect(() => attach(), [attach]);
  useEffect(() => attachReactions(), [attachReactions]);

  function sendReaction(kind: ReactionKind) {
    if (!gameId) return;
    getSocket().emit('game:reaction', { gameId, kind });
    haptic('select', settings.haptic);
  }

  useEffect(() => {
    if (!gameId) return;
    const sock = getSocket();
    sock.emit('game:sync', { gameId });
    refreshActive();
    return () => {
      // Leaving this game's page — drop its board and chat so the next game
      // doesn't start out rendering the previous one, and so a reconnect
      // doesn't re-sync a game we already left.
      setView(null);
      clearChat();
    };
  }, [gameId, refreshActive, setView, clearChat]);

  // Keep the resume-banner state in sync with the live phase
  useEffect(() => {
    if (view?.phase === 'finished') clearActive();
    else if (view?.phase === 'playing' || view?.phase === 'lobby') refreshActive();
  }, [view?.phase, view?.gameId, refreshActive, clearActive]);


  // Celebrate on round end if the viewer survived (was not the durak).
  useEffect(() => {
    if (view?.phase !== 'finished') return;
    if (view.durakSeat === view.yourSeat) return; // you lost — no chime
    playSfx('chime', settings.sound);
  }, [view?.phase, view?.durakSeat, view?.yourSeat, settings.sound]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(t);
    }
  }, [error, setError]);

  const isHost = view && me && view.players.find(p => p.id === me.id)?.isHost;
  const yourSeat = view?.yourSeat ?? -1;
  const isAttacker = view?.attackerSeat === yourSeat;
  const isDefender = view?.defenderSeat === yourSeat;
  const youCanAct = view?.toActSeats.includes(yourSeat) ?? false;
  const phase = view?.phase ?? 'lobby';

  // Tag the slots with a "fly upward on exit" hint when an opponent has
  // committed to taking. The slots get this prop on the last render before
  // resolveTurn clears the table, so when AnimatePresence snapshots their
  // exit state, the fly-to-opponent animation is what runs.
  // Local takes are skipped — framer-motion's `layoutId` already animates
  // the cards from the table into the local player's hand.
  const takenByOpponent = !!view?.takingDefender
    && view?.defenderSeat !== null
    && view?.defenderSeat !== view?.yourSeat;
  // Local viewer is the taker — the slot wrapper should fade quietly so each
  // card's shared-layoutId animation into the hand is what plays.
  const takenByYou = !!view?.takingDefender
    && view?.defenderSeat !== null
    && view?.defenderSeat === view?.yourSeat;

  // Compute which hand cards are playable
  const playableIds = useMemo(() => {
    if (!view || !youCanAct || phase !== 'playing') return new Set<string>();
    const set = new Set<string>();
    if (isDefender && !view.table.every(s => s.defense)) {
      // pick any card; legality checked per-slot
      view.hand.forEach(c => set.add(c.id));
    } else if (view.toActSeats.includes(yourSeat) && (isAttacker || (view.table.length > 0))) {
      // attacker / throw-in: rank must be on table, unless table empty
      if (view.table.length === 0) {
        view.hand.forEach(c => set.add(c.id));
      } else {
        const ranks = new Set<string>();
        view.table.forEach(s => { ranks.add(s.attack.rank); if (s.defense) ranks.add(s.defense.rank); });
        view.hand.forEach(c => { if (ranks.has(c.rank)) set.add(c.id); });
      }
    }
    return set;
  }, [view, youCanAct, phase, isAttacker, isDefender, yourSeat]);

  function send(move: Move) {
    if (!gameId) return;
    const sock = getSocket();
    sock.emit('game:move', { gameId, move }, (res) => {
      if (!res?.ok) {
        setError(res?.error || 'Move rejected');
        haptic('error', settings.haptic);
        playSfx('error', settings.sound);
      } else {
        setSelected(null);
        haptic('success', settings.haptic);
        playSfx(move.kind === 'defend' ? 'defend' : move.kind === 'attack' || move.kind === 'throwIn' || move.kind === 'transfer' ? 'play' : 'play', settings.sound);
      }
    });
  }

  /** Best-move dispatcher used by swipe / drag commits.
   * Defender gestures:
   *   - drop on an attack slot       → defend that slot
   *   - drop in the table (not slot) → transfer
   *   - swipe up (no drop target)    → defend first undefended slot
   */
  function commitCard(card: Card, dropTarget: import('../components/Hand').DropTarget | null) {
    if (!view) return;
    if (isDefender) {
      if (dropTarget?.kind === 'slot' && !view.table[dropTarget.slotIndex]?.defense) {
        send({ kind: 'defend', slotIndex: dropTarget.slotIndex, card });
        return;
      }
      // A drop "next to the slots" is only a transfer when transfer is
      // actually legal here (rule enabled, all undefended, matching rank…).
      // Otherwise treat it as a defend gesture — a near-miss drop must not
      // surface a "Transfer not allowed" rejection.
      if (dropTarget?.kind === 'table' && canTransferWith(view, card)) {
        send({ kind: 'transfer', card });
        return;
      }
      // Pick the first undefended slot this card can actually beat — picking
      // blindly causes "does not beat the attack" rejections when slot 0 is
      // a card the player intended to leave for a different defender card.
      const deckSize = view.options.deckSize;
      const idx = view.table.findIndex(s => !s.defense && beats(s.attack, card, view.trumpSuit, deckSize));
      if (idx >= 0) {
        send({ kind: 'defend', slotIndex: idx, card });
        return;
      }
      // Defend isn't possible with this card — if transfer is the only legal play,
      // do it automatically.
      if (canTransferWith(view, card)) {
        send({ kind: 'transfer', card });
        return;
      }
      // Last resort: let the server reject against the first undefended slot
      // so the user sees a clear error message.
      const fallback = view.table.findIndex(s => !s.defense);
      if (fallback >= 0) {
        send({ kind: 'defend', slotIndex: fallback, card });
        return;
      }
    }
    // Otherwise attack or throw in
    if (view.table.length === 0) {
      send({ kind: 'attack', card });
    } else {
      send({ kind: 'throwIn', card });
    }
  }

  function onCardSelect(c: Card) {
    if (!view) return;
    setSelected(prev => prev?.id === c.id ? null : c);

    // If defender and a slot is active, attempt to defend
    if (isDefender && activeSlot !== null) {
      send({ kind: 'defend', slotIndex: activeSlot, card: c });
      setActiveSlot(null);
      return;
    }
    // If attacker and table empty → attack on click again
  }

  function onSlotClick(idx: number) {
    if (!view) return;
    if (isDefender && !view.table[idx].defense) {
      if (selected) {
        send({ kind: 'defend', slotIndex: idx, card: selected });
      } else {
        setActiveSlot(idx);
      }
    }
  }

  function playSelected() {
    if (!selected || !view) return;
    commitCard(selected, null);
  }

  const shareUrl = view ? `${window.location.origin}/game/${view.gameId}` : '';
  const codeUrl = view ? `${window.location.origin}/g/${view.code}` : '';

  if (!view) {
    return (
      <AppShell>
        <div className="panel p-8 text-center">
          <div className="animate-pulse text-ink-200">Connecting to game…</div>
          <button className="btn-ghost mt-4" onClick={() => nav('/')}>Back to home</button>
        </div>
      </AppShell>
    );
  }

  const youPlayer = view.players.find(p => p.id === me?.id);
  const trumpSuit = view.trumpSuit;
  const otherPlayers = view.players.filter(p => p.id !== me?.id);

  return (
    <AppShell>
      {/* Error toast */}
      <AnimatePresence>
        {error && (
          <div
            className="fixed left-0 right-0 z-50 flex justify-center pointer-events-none"
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
              className="bg-rose-500/95 text-white px-4 py-2 rounded-lg shadow-lg text-sm"
            >{error}</motion.div>
          </div>
        )}
      </AnimatePresence>

      {phase === 'lobby' && (
        <Lobby view={view} isHost={!!isHost} onStart={() => {
          getSocket().emit('game:start', { gameId: view.gameId }, res => { if (!res?.ok) setError(res?.error || 'Could not start'); });
        }} onLeave={() => {
          getSocket().emit('game:leave', { gameId: view.gameId });
          clearActive();
          nav('/');
        }} />
      )}

      {phase === 'playing' && view.players.some(p => p.isBot) && (
        <div className="flex justify-center -mb-1">
          <span className="text-[10px] font-display tracking-[0.18em] uppercase text-ink-300 border border-ink-600/70 px-3 py-1 rounded-full">
            Practice · Unranked
          </span>
        </div>
      )}
      {phase === 'playing' && (
        <div className="flex-1 min-h-0 flex flex-col gap-2 sm:gap-3">
          {/* top row: opponents — pt-10 reserves room for reaction bubbles to
              bleed upward out of each seat. overflow-x-auto on this element
              also clips overflow-y (per CSS spec), so the vertical padding
              must be tall enough to fit the bubble before it gets clipped. */}
          <div className={clsx(
            'shrink-0 flex gap-2 overflow-x-auto pt-10 pb-1 px-1 -mx-2',
            settings.rotateSeats && view.options.rotateSeats && 'justify-around',
          )}>
            {otherPlayers.map((p) => {
              const r = latestReactionFor(reactions, p.id);
              return (
                <Seat
                  key={p.id}
                  player={p}
                  isAttacker={view.attackerSeat === p.seat}
                  isDefender={view.defenderSeat === p.seat}
                  toAct={view.toActSeats.includes(p.seat)}
                  reaction={r ? { kind: r.kind, id: r.id } : null}
                  moveDeadline={view.toActSeats.includes(p.seat) ? view.moveDeadline : null}
                />
              );
            })}
          </div>

          {/* The felt table itself */}
          <div className="felt-table relative flex-1 min-h-0 flex flex-col px-3 pt-3 pb-3 sm:px-5 sm:pt-4 sm:pb-4">
            {/* Faint suit watermarks in the corners */}
            <div className="felt-watermark">
              <span style={{ top: 8, left: 14, fontSize: 48, opacity: 0.5 }}>♠</span>
              <span style={{ bottom: 8, right: 14, fontSize: 48, opacity: 0.5, transform: 'rotate(180deg)' }}>♣</span>
            </div>

            {/* Played pairs centered — fills remaining felt height so cards have
                room to wrap onto a second row when the table fills up, while
                shrinking on short screens to keep the hand on-screen. */}
            <div className="relative flex-1 min-h-0 flex items-center justify-center py-1 overflow-hidden">
              <Table
                slots={view.table}
                onSlotClick={onSlotClick}
                activeSlotIdx={activeSlot}
                slotRectsRef={slotRectsRef}
                tableRectRef={tableRectRef}
                exitToOpponent={takenByOpponent}
                exitToLocal={takenByYou}
                highlightSlots={isDefender ? new Set(view.table.map((s, i) => s.defense ? -1 : i).filter(i => i >= 0)) : undefined}
                showTransferZone={(() => {
                  if (!isDefender || !view.options.transferable) return false;
                  if (view.table.length === 0 || view.table.some(s => s.defense)) return false;
                  const engaged = draggingCard || selected;
                  if (!engaged) return false;
                  // The engaged card must match the rank shared by every (undefended) attack.
                  return view.table.every(s => s.attack.rank === engaged.rank);
                })()}
              />
            </div>

            {/* Bottom strip: deck/trump on left, action button on right */}
            <div className="shrink-0 mt-2 sm:mt-3 flex items-center justify-between gap-3 relative">
              {view.deckCount > 0 ? (
                <div className="flex items-center gap-3">
                  {/* Talon deck on the LEFT, trump card peeking out to its right.
                      Deck sits on top in z-order so the trump appears tucked under it. */}
                  <div className="flex items-center">
                    {/* Face-down deck stack — three offset cards give a real-deck feel */}
                    <div className="relative" style={{ zIndex: 2 }}>
                      <div className="absolute -left-2 -top-1 opacity-50"><PlayingCard faceDown small /></div>
                      <div className="absolute -left-1 -top-0.5 opacity-80"><PlayingCard faceDown small /></div>
                      <PlayingCard faceDown small />
                    </div>
                    {/* Rotated trump tucked under the deck's right edge.
                        +90deg rotation puts the rank/suit corner on the visible (right) side.
                        Container is wide enough to contain the full visual span so the
                        next flex item (talon count) doesn't get overlapped. */}
                    <div
                      className="relative"
                      style={{ width: 110, height: 60, marginLeft: -28, marginRight: 4, zIndex: 1 }}
                    >
                      <div
                        className="absolute"
                        style={{
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%) rotate(90deg)',
                          transformOrigin: 'center',
                        }}
                      >
                        {view.trump && <PlayingCard card={view.trump} small />}
                      </div>
                    </div>
                  </div>
                  <motion.div
                    key={view.deckCount}
                    initial={{ scale: 1.3 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                    className="flex flex-col items-start leading-none"
                  >
                    <span className="text-2xl font-display font-bold text-accent">{view.deckCount}</span>
                    <span className="text-[10px] uppercase tracking-wider text-ink-200/70 mt-0.5">in talon</span>
                  </motion.div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {view.trump && (
                    <div className="px-3 py-2 rounded-xl bg-ink-900/40 border border-accent/30 text-accent font-display font-bold flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-ink-200/70 mr-1">Trump</span>
                      <span className="text-lg">{view.trump.rank}{suitSym(view.trump.suit)}</span>
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-wider text-ink-200/70">Discard {view.discardCount}</div>
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap justify-end">
                {youCanAct && (
                  <>
                    {isDefender && view.table.some(s => !s.defense) && (
                      <button className="btn-secondary !py-2 !px-4" onClick={() => send({ kind: 'take' })}>Take</button>
                    )}
                    {!isDefender && view.table.length > 0 && (
                      <button className="btn-secondary !py-2 !px-4" onClick={() => send({ kind: 'endTurn' })}>Pass</button>
                    )}
                    {selected && (
                      <button className="btn-primary !py-2 !px-4" onClick={playSelected}>Play card</button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* your hand
              z-20 lifts the whole hand panel above the felt's stacking context
              so a dragged card (which sits inside .panel's backdrop-filter
              stacking context) can visually overlap the played cards on the
              table instead of being painted under them.
              When it's your turn we layer a pulsing gold ring + "YOUR TURN"
              chip with the move countdown so the cue is unmissable. */}
          <div className={clsx(
            'shrink-0 panel p-2 overflow-visible relative z-20 transition',
            youCanAct && 'your-turn-glow',
          )}>
            {youCanAct && (
              <div className="pointer-events-none absolute -top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 bg-accent text-ink-950 text-[10px] font-display font-bold uppercase tracking-[0.18em] pl-3 pr-1.5 py-0.5 rounded-full shadow-glow">
                <span>Your Turn</span>
                {view.moveDeadline && <TurnCountdownRing deadline={view.moveDeadline} />}
              </div>
            )}
            <div className="flex items-center justify-between gap-2 px-2 pt-1 pb-1.5 border-b border-ink-700/40">
              <div className="text-[11px] sm:text-xs text-ink-200/80 truncate">
                {settings.showHints ? (
                  isDefender
                    ? (view.options.transferable && view.table.every(s => !s.defense)
                        ? 'Drag onto an attack · drop next to it to transfer'
                        : 'Drag a card onto an attack, or swipe up to defend')
                    : isAttacker && view.table.length === 0 ? 'Your turn — swipe a card up to attack'
                    : view.table.length > 0 && youCanAct ? 'Throw in — swipe up a matching rank'
                    : 'Waiting…'
                ) : (
                  isDefender ? 'Defend' : isAttacker && view.table.length === 0 ? 'Your turn' : view.table.length > 0 && youCanAct ? 'Throw in' : 'Waiting…'
                )}
              </div>
              <div className="text-[11px] sm:text-xs text-ink-200/80 shrink-0">
                {trumpSuit && <span>Trump <span className="text-accent font-semibold">{suitSym(trumpSuit)}</span></span>}
              </div>
            </div>
            <Hand
              cards={view.hand}
              selectedId={selected?.id}
              onSelect={(c) => { haptic('select', settings.haptic); onCardSelect(c); }}
              onCommit={(c, target) => { setDraggingCard(null); commitCard(c, target); }}
              onDragStart={(c) => setDraggingCard(c)}
              onDragEnd={() => setDraggingCard(null)}
              trumpSuit={trumpSuit}
              disabledIds={new Set(view.hand.filter(c => !playableIds.has(c.id)).map(c => c.id))}
              slotRects={slotRectsRef.current}
              tableRectRef={tableRectRef}
            />
          </div>

          {/* footer actions
              z-30 (above the hand's z-20) so the ReactionPicker popover —
              which is position:fixed inside this panel's backdrop-filter
              stacking context — paints above the hand cards. */}
          <div className="shrink-0 panel p-2 sm:p-3 flex flex-wrap items-center gap-2 relative z-30">
            <ReactionPicker onSend={sendReaction} />
            <button className="btn-ghost !py-1.5" onClick={() => setShowInvite(true)}>Invite</button>
            <button className="btn-ghost !py-1.5" onClick={() => setChatOpen(s => !s)}>{chatOpen ? 'Hide' : 'Show'} chat</button>
            <button className="btn-ghost !py-1.5 ml-auto" onClick={() => {
              getSocket().emit('game:leave', { gameId: view.gameId });
              clearActive();
              nav('/');
            }}>Leave</button>
          </div>

          {chatOpen && (
            <ChatPanel chat={chat} onSend={(t) => {
              const text = t.trim(); if (!text) return;
              getSocket().emit('game:chat', { gameId: view.gameId, text });
              setChatText('');
            }} value={chatText} onChange={setChatText} />
          )}
        </div>
      )}

      {phase === 'finished' && (
        <>
          <Confetti active={view.durakSeat !== yourSeat} />
          <ReadyCheckPanel
            view={view}
            onReady={() => {
              getSocket().emit('game:ready', { gameId: view.gameId }, (res) => {
                if (!res?.ok) setError(res?.error || 'Could not mark ready');
              });
            }}
            onLeave={() => {
              getSocket().emit('game:leave', { gameId: view.gameId });
              clearActive();
              nav('/');
            }}
          />
        </>
      )}

      {showInvite && view && (
        <InviteModal
          gameId={view.gameId}
          shareUrl={shareUrl}
          codeUrl={codeUrl}
          onClose={() => setShowInvite(false)}
        />
      )}

      {/* Sender's own reaction — floats above their hand so they see it too */}
      <AnimatePresence>
        {me && (() => {
          const mine = latestReactionFor(reactions, me.id);
          if (!mine) return null;
          return (
            <motion.div
              key={mine.id}
              initial={{ opacity: 0, y: 30, scale: 0.5 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -28, scale: 0.85 }}
              transition={{ type: 'spring', stiffness: 260, damping: 18 }}
              className="fixed left-1/2 -translate-x-1/2 z-30 pointer-events-none select-none flex flex-col items-center"
              style={{
                /* Sit just above the hand panel so we don't cover the cards */
                bottom: 'calc(env(safe-area-inset-bottom) + 240px)',
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))',
              }}
            >
              <ReactionArt kind={mine.kind} size={56} />
              <span className="text-[10px] text-ink-300 mt-1 bg-ink-900/70 px-2 py-0.5 rounded-full">you</span>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </AppShell>
  );
}

function latestReactionFor(reactions: { id: number; fromId: string; kind: ReactionKind; ts: number }[], userId: string) {
  let best: { id: number; fromId: string; kind: ReactionKind; ts: number } | null = null;
  for (const r of reactions) {
    if (r.fromId !== userId) continue;
    if (!best || r.ts > best.ts) best = r;
  }
  return best;
}

function suitSym(s: string) {
  return s === 'spades' ? '♠' : s === 'hearts' ? '♥' : s === 'diamonds' ? '♦' : '♣';
}

function Lobby({ view, isHost, onStart, onLeave }: { view: any; isHost: boolean; onStart: () => void; onLeave: () => void }) {
  const [showRules, setShowRules] = useState(false);
  return (
    <div className="panel p-6 space-y-4">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <div className="text-ink-300 text-sm">Waiting room</div>
          <h2 className="font-display text-2xl font-bold">Share the code with friends</h2>
        </div>
        {view.options.isPrivate && (
          <span className="chip bg-accent/20 text-accent border-accent/30">
            🔒 Private
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="text-3xl font-mono tracking-widest text-accent">{view.code || '------'}</div>
        <button className="btn-ghost !py-1.5" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/g/${view.code}`)}>
          Copy invite link
        </button>
      </div>
      <div>
        <div className="text-sm text-ink-300 mb-2">{view.players.length}/{view.options.maxPlayers} players</div>
        <ul className="grid sm:grid-cols-2 gap-2">
          {view.players.map((p: any) => (
            <li key={p.id} className="panel !p-3 flex items-center gap-3">
              <Avatar src={p.avatar} name={p.name} size={36} />
              <div className="font-semibold flex items-center gap-1">
                {p.name}
                {p.isBot && <span className="chip !text-[10px] bg-ink-700">BOT</span>}
              </div>
              {p.isHost && <span className="ml-auto chip">HOST</span>}
              {p.isBot && isHost && (
                <button
                  className="btn-ghost !p-1 !px-2 text-xs ml-auto text-ink-300"
                  onClick={() => getSocket().emit('game:removeBot', { gameId: view.gameId, botId: p.id })}
                  aria-label={`Remove ${p.name}`}
                >×</button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Rules summary visible to everyone; host can edit */}
      <div className="rounded-xl border border-ink-700/60 p-3">
        <div className="flex items-center gap-2">
          <div className="section-label flex-1">Game rules</div>
          {isHost && (
            <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => setShowRules(s => !s)}>
              {showRules ? 'Done' : 'Edit'}
            </button>
          )}
        </div>
        {showRules && isHost ? (
          <div className="mt-2">
            <GameOptionsPanel
              value={view.options}
              onChange={(partial) => {
                getSocket().emit('game:options', { gameId: view.gameId, options: partial });
              }}
              hidePrivate
              minMaxPlayers={Math.max(2, view.players.length)}
            />
          </div>
        ) : (
          <RulesSummary opts={view.options} />
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {isHost
          ? <button className="btn-primary" onClick={onStart} disabled={view.players.length < 2}>Start game</button>
          : <div className="text-sm text-ink-300">Waiting for the host to start…</div>
        }
        {isHost && view.players.length < view.options.maxPlayers && (
          <button
            className="btn-secondary"
            onClick={() => getSocket().emit('game:addBot', { gameId: view.gameId })}
          >
            🤖 Add bot
          </button>
        )}
        <button className="btn-ghost ml-auto" onClick={onLeave}>Leave</button>
      </div>
    </div>
  );
}

function RulesSummary({ opts }: { opts: import('@durak/shared').GameOptions }) {
  const items: string[] = [
    `${opts.deckSize}-card deck`,
    `${opts.maxPlayers} max players`,
    opts.transferable ? 'Perevodnoy transfer' : 'No transfer',
    opts.throwInByAll ? 'Throw-in by all' : 'Throw-in by neighbours',
    opts.refillHands ? 'Refill from talon' : 'No refill',
    `Max ${opts.maxAttackCards} attack cards`,
  ];
  return (
    <ul className="mt-2 flex flex-wrap gap-1.5">
      {items.map((t) => <li key={t} className="text-[11px] text-ink-200 bg-ink-800/60 border border-ink-700 px-2 py-0.5 rounded-full">{t}</li>)}
    </ul>
  );
}

function ReadyCheckPanel({ view, onReady, onLeave }: { view: any; onReady: () => void; onLeave: () => void }) {
  const durak = view.durakSeat !== null ? view.players[view.durakSeat] : null;
  const finishers = view.finishOrder.map((seat: number) => view.players[seat]);
  const readySet: Set<string> = new Set(view.readyUserIds ?? []);
  const me = view.players.find((p: any) => p.seat === view.yourSeat);
  const youAreReady = !!me && readySet.has(me.id);
  // Bots are implicitly ready; humans gate the auto-start.
  const humans = view.players.filter((p: any) => !p.isBot);
  const humansReady = humans.filter((p: any) => readySet.has(p.id)).length;
  return (
    <div className="panel p-8 text-center space-y-5 animate-float-in">
      <div className="text-6xl">{durak ? '🏆' : '🤝'}</div>
      <h2 className="font-display text-2xl font-bold">
        {durak ? `${durak.name} is the Durak!` : 'Draw — no Durak this round.'}
      </h2>
      {finishers.length > 0 && (
        <div>
          <div className="text-sm text-ink-300 mb-2">Finish order</div>
          <ol className="flex flex-wrap justify-center gap-2">
            {finishers.map((p: any, i: number) => (
              <li key={p.id} className="chip">{i + 1}. {p.name}</li>
            ))}
          </ol>
        </div>
      )}
      <div className="pt-1">
        <div className="text-sm text-ink-200 mb-2">Ready for the next round?</div>
        <ul className="flex flex-wrap justify-center gap-2 mb-1">
          {view.players.map((p: any) => {
            const isReady = p.isBot || readySet.has(p.id);
            return (
              <li
                key={p.id}
                className={clsx(
                  'chip transition',
                  isReady ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' : 'text-ink-200',
                )}
                title={p.isBot ? 'Bots are ready automatically' : isReady ? 'Ready' : 'Waiting…'}
              >
                {isReady ? '✓' : '•'} {p.name}{p.isBot ? ' (bot)' : ''}
              </li>
            );
          })}
        </ul>
        <div className="text-[11px] text-ink-300">
          {humansReady} / {humans.length} ready
        </div>
      </div>
      <div className="pt-2 flex flex-wrap justify-center gap-2">
        <button className="btn-primary" disabled={youAreReady} onClick={onReady}>
          {youAreReady ? 'Waiting for others…' : 'Ready'}
        </button>
        <button className="btn-ghost" onClick={onLeave}>Leave</button>
      </div>
    </div>
  );
}

function ChatPanel({ chat, onSend, value, onChange }: { chat: { name: string; text: string; ts: number }[]; onSend: (t: string) => void; value: string; onChange: (v: string) => void }) {
  const listRef = useRef<HTMLDivElement>(null);
  // Keep the newest message visible — without this, once history exceeds the
  // box every incoming message renders below the fold.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat]);
  return (
    <div className="panel p-3">
      <div ref={listRef} className="max-h-40 overflow-y-auto space-y-1 mb-2 text-sm">
        {chat.length === 0 && <div className="text-ink-300 italic text-xs">No messages yet</div>}
        {chat.map((m, i) => (
          <div key={i}><span className="text-accent font-semibold">{m.name}</span>: <span>{m.text}</span></div>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); onSend(value); }} className="flex gap-2">
        <input className="input" placeholder="Say something…" value={value} onChange={e => onChange(e.target.value)} maxLength={200} />
        <button className="btn-primary !py-1.5">Send</button>
      </form>
    </div>
  );
}

function InviteModal({ gameId, codeUrl, shareUrl, onClose }: { gameId: string; codeUrl: string; shareUrl: string; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Array<{ id: string; name: string; avatar: string | null }>>([]);
  const [busy, setBusy] = useState(false);
  const friends = useFriends(s => s.friends);
  const refreshFriends = useFriends(s => s.refresh);
  const friendsLoaded = useFriends(s => s.loaded);

  useEffect(() => {
    if (!friendsLoaded) refreshFriends().catch(() => {});
  }, [friendsLoaded, refreshFriends]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setResults([]); return; }
      try {
        const r = await api.searchUsers(q.trim());
        // A slower, older response must not overwrite a newer query's results.
        if (!cancelled) setResults(r);
      } catch { /* ignore */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  async function shareNative() {
    if ((navigator as any).share) {
      try {
        await (navigator as any).share({ title: 'Durak game', text: 'Join my Durak game!', url: codeUrl || shareUrl });
      } catch {}
    } else {
      navigator.clipboard.writeText(codeUrl || shareUrl).catch(() => {});
    }
  }

  function notifyUser(userId: string) {
    setBusy(true);
    getSocket().emit('push:invite', { gameId, toUserId: userId }, () => setBusy(false));
  }

  return (
    <div className="fixed inset-0 bg-ink-950/80 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="panel p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <h3 className="font-display text-lg font-bold mb-3">Invite friends</h3>
        <div className="space-y-3">
          <button className="btn-primary w-full" onClick={shareNative}>Share link</button>

          {friends.length > 0 && (
            <div>
              <div className="section-label mb-1">Friends</div>
              <ul className="max-h-44 overflow-y-auto divide-y divide-ink-700">
                {friends.map(f => (
                  <li key={f.id} className="flex items-center gap-2 py-2">
                    <div className="relative">
                      <Avatar src={f.avatar} name={f.name} size={32} />
                      <span className={clsx('absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-ink-800', f.online ? 'bg-emerald-400' : 'bg-zinc-500')} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{f.name}</div>
                      <div className="text-[10px] text-ink-300">{f.online ? 'online' : 'offline'}</div>
                    </div>
                    <button className="btn-secondary !py-1 !px-2 text-xs" disabled={busy} onClick={() => notifyUser(f.id)}>Notify</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className="section-label mb-1">Or search</div>
            <input className="input" placeholder="Search by name" value={q} onChange={e => setQ(e.target.value)} />
            <ul className="max-h-40 overflow-y-auto divide-y divide-ink-700 mt-1">
              {results.map(u => (
                <li key={u.id} className="flex items-center gap-2 py-2">
                  <Avatar src={u.avatar} name={u.name} size={32} />
                  <div className="flex-1">{u.name}</div>
                  <button className="btn-secondary !py-1 !px-2 text-xs" disabled={busy} onClick={() => notifyUser(u.id)}>Notify</button>
                </li>
              ))}
              {results.length === 0 && q.length >= 2 && (
                <li className="text-ink-300 text-sm py-2">No users found</li>
              )}
            </ul>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
